import { createServerFn } from '@tanstack/react-start';
import { getCookie } from '@tanstack/react-start/server';
import { queryOptions } from '@tanstack/react-query';
import { createPresignedUploadUrl, createPresignedCdnUploadUrl } from '@worldcoo/monolith/apis/backoffice/server';
import * as z from 'zod';
import { requireSession, UNAUTHENTICATED, verifySignedCookie } from './server-functions-shared.server';

// Emails allowed to use the impersonation feature (server-side only)
const ADMIN_EMAILS = new Set([
	'romulo.ochoa@worldcoo.com',
	'aitor.guerrero@worldcoo.com',
	'cristina.duran@worldcoo.com',
]);

export const validateAdminSessionFn = createServerFn().handler(() => {
	const adminEmail = verifySignedCookie(getCookie('ngo-admin-session'));
	if (adminEmail && ADMIN_EMAILS.has(adminEmail)) {
		return { authenticated: true, adminEmail } as const;
	}
	return { authenticated: false } as const;
});

export const validateAdminSessionOptions = () =>
	queryOptions({
		queryKey: ['validateAdminSession'],
		queryFn: () => validateAdminSessionFn(),
	});

const uploadFileTypes = {
	// Project files → CDN bucket
	coverVideo: { prefix: (id: string) => `projects/${id}/cover-video`, bucket: 'cdn' },
	coverImage: { prefix: (id: string) => `projects/${id}/cover`, bucket: 'cdn' },
	otherImage: { prefix: (id: string) => `projects/${id}/images`, bucket: 'cdn' },
	// NGO files
	ngoLogo: { prefix: (id: string) => `ngos/${id}/logo`, bucket: 'cdn' },
	ngoCertificate: { prefix: (id: string) => `ngos/${id}/certificate`, bucket: 'backoffice' },
	// Portal files → CDN bucket
	portalCoverImage: { prefix: (id: string) => `applications/${id}`, bucket: 'cdn' },
	portalLogo: { prefix: (id: string) => `applications/${id}/logo`, bucket: 'cdn' },
	portalSliderImage: { prefix: (id: string) => `applications/${id}/slider`, bucket: 'cdn' },
} as const;

export const getPresignedUploadUrlFn = createServerFn({ method: 'POST' })
	.inputValidator(
		z.object({
			entityId: z.string().optional(),
			contentType: z.string(),
			fileType: z.enum([
				'coverVideo',
				'coverImage',
				'otherImage',
				'ngoLogo',
				'ngoCertificate',
				'portalCoverImage',
				'portalLogo',
				'portalSliderImage',
			]),
		}),
	)
	.handler(async ({ data: { entityId, contentType, fileType } }) => {
		const session = requireSession();
		if ('unauthenticated' in session) return UNAUTHENTICATED;
		const { ngoId } = session;
		const config = uploadFileTypes[fileType];
		const prefix = config.prefix(entityId ?? ngoId);
		const fn = config.bucket === 'cdn' ? createPresignedCdnUploadUrl : createPresignedUploadUrl;
		const result = await fn(prefix, contentType);
		if (!result) return { error: 'INVALID_CONTENT_TYPE' as const };
		return result;
	});
